import React from 'react';

import Slider from 'react-slick';

import './yellow-card-style.css';

interface CTA {
  text: string;
  href?: string;
  onClick?: () => void;
}

interface BaseCard {
  headline: string;
  description: string;
  primaryCta?: CTA;
  secondaryCta?: CTA;
  headlineColor?: 'white' | 'yellow' | 'yellow-white' | 'white-yellow';
}

interface YellowCardGridProps {
  cards?: BaseCard[];
}

interface YellowCardComponentProps {
  card: BaseCard;
}

function renderHeadline(headline: string, color?: BaseCard['headlineColor']) {
  const baseClasses =
    'au-text-base md:au-text-lg au-font-bold au-mb-3 md:au-mb-4';

  switch (color) {
    case 'yellow':
      return <h3 className={`${baseClasses} au-text-[#FEFF09]`}>{headline}</h3>;
    case 'yellow-white': {
      const words = headline.split(' ');
      const firstWord = words[0];
      const restWords = words.slice(1).join(' ');
      return (
        <h3 className={baseClasses}>
          <span className="au-text-[#FEFF09]">{firstWord}</span>
          {restWords && <span className="au-text-white"> {restWords}</span>}
        </h3>
      );
    }
    case 'white-yellow': {
      const words = headline.split(' ');
      const firstWord = words[0];
      const restWords = words.slice(1).join(' ');
      return (
        <h3 className={baseClasses}>
          <span className="au-text-white">{firstWord}</span>
          {restWords && <span className="au-text-[#FEFF09]"> {restWords}</span>}
        </h3>
      );
    }
    case 'white':
    default:
      return <h3 className={`${baseClasses} au-text-white`}>{headline}</h3>;
  }
}

function YellowCardComponent({ card }: YellowCardComponentProps) {
  const hasCtaButtons = card.primaryCta || card.secondaryCta;

  return (
    <div className="au-yellow-card au-bg-[#161616] au-border au-border-[#FEFF09] au-rounded-lg au-p-4 md:au-p-6 au-h-[260px] md:au-h-[280px] au-flex au-flex-col">
      {renderHeadline(card.headline, card.headlineColor)}
      <p
        className={`au-text-white au-text-sm au-leading-relaxed au-mb-4 md:au-mb-6 ${
          hasCtaButtons
            ? 'au-description-clamp'
            : 'au-flex-grow au-overflow-y-auto'
        }`}
      >
        {card.description}
      </p>

      <div className="au-flex au-gap-2 md:au-gap-3 au-flex-wrap au-mt-auto">
        {card.primaryCta && (
          <button
            className="au-border au-border-white au-text-white au-px-3 md:au-px-4 au-py-1 md:au-py-2 au-rounded au-text-xs md:au-text-sm au-hover:au-bg-white au-hover:au-text-slate-900 au-transition-colors au-flex-1 md:au-flex-none"
            onClick={card.primaryCta.onClick}
            type="button"
          >
            {card.primaryCta.text}
          </button>
        )}
        {card.secondaryCta && (
          <button
            className="au-border au-border-white au-text-white au-px-3 md:au-px-4 au-py-1 md:au-py-2 au-rounded au-text-xs md:au-text-sm au-hover:au-bg-white au-hover:au-text-slate-900 au-transition-colors au-flex-1 md:au-flex-none"
            onClick={card.secondaryCta.onClick}
            type="button"
          >
            {card.secondaryCta.text}
          </button>
        )}
      </div>
    </div>
  );
}

function YellowCardGrid({ cards = [] }: YellowCardGridProps) {
  const sliderSettings = {
    dots: false,
    infinite: cards.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: cards.length > 3,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
    ],
  };

  // If 3 or fewer cards, display as grid
  if (cards.length <= 3) {
    return (
      <div className="au-grid au-grid-cols-1 md:au-grid-cols-2 xl:au-grid-cols-3 au-gap-4 md:au-gap-6 au-p-4 md:au-p-6">
        {cards.map(card => (
          <YellowCardComponent key={card.headline} card={card} />
        ))}
      </div>
    );
  }

  // If more than 3 cards, display as carousel
  return (
    <div className="au-yellow-card-carousel au-px-4 md:au-px-6 au-py-4">
      <Slider {...sliderSettings}>
        {cards.map(card => (
          <div key={card.headline} className="au-px-2 md:au-px-3">
            <YellowCardComponent card={card} />
          </div>
        ))}
      </Slider>
    </div>
  );
}

// Define attributes for AURR system
YellowCardGrid.attributes = {
  cards: {
    type: 'array',
    default: [],
    properties: {
      headline: { type: 'string' },
      description: { type: 'string' },
      headlineColor: {
        type: 'string',
        enum: ['white', 'yellow', 'yellow-white', 'white-yellow'],
        default: 'white',
      },
      primaryCta: {
        type: 'object',
        properties: {
          text: { type: 'string' },
          href: { type: 'string' },
        },
      },
      secondaryCta: {
        type: 'object',
        properties: {
          text: { type: 'string' },
          href: { type: 'string' },
        },
      },
    },
  },
};

export default YellowCardGrid;
